import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';

const Notifications = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch notifications
  const { data: notificationsData, isLoading, error: notificationsError } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications');
      if (!response.data.success) {
        console.error('❌ Notifications API error:', response.data);
        return { notifications: [], unread_count: 0 };
      }
      return response.data.data || { notifications: [], unread_count: 0 };
    },
    refetchInterval: 10000, // Her 10 saniyede bir kontrol et (gerçek zamanlı bildirim)
    enabled: !!user,
    onError: (error) => {
      console.error('❌ Notifications fetch error:', error);
    }
  });

  const notifications = notificationsData?.notifications || [];
  const unreadCount = notificationsData?.unread_count || 0;

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId) => {
      return api.post(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
    }
  });

  // Dismiss notification mutation
  const dismissMutation = useMutation({
    mutationFn: async (notificationId) => {
      return api.post(`/notifications/${notificationId}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
    }
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }

    // Navigate based on type
    if (notification.type === 'mobile_case') {
      navigate('/mobile-cases');
    } else if (notification.type === 'planned_case' || notification.type === 'unassigned_case' || notification.type === 'team_assignment' || notification.type === 'presidency_case_assignment') {
      navigate(`/cases/${notification.related_id}`);
    } else if (notification.type === 'organization') {
      navigate(`/organizations/${notification.related_id}`);
    } else if (notification.type === 'system') {
      navigate('/settings');
    }

    setIsOpen(false);
  };

  const handleDismiss = (e, notificationId) => {
    e.stopPropagation();
    dismissMutation.mutate(notificationId);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'mobile_case':
        return '📱';
      case 'planned_case':
        return '📅';
      case 'organization':
        return '🏢';
      case 'unassigned_case':
        return '⚠️';
      case 'team_assignment':
        return '🚑';
      case 'presidency_case_assignment':
        return '🏛️';
      case 'system':
        return '✨';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'mobile_case':
        return 'bg-blue-100 text-blue-800';
      case 'planned_case':
        return 'bg-orange-100 text-orange-800';
      case 'organization':
        return 'bg-purple-100 text-purple-800';
      case 'unassigned_case':
        return 'bg-red-100 text-red-800';
      case 'team_assignment':
        return 'bg-green-100 text-green-800';
      case 'presidency_case_assignment':
        return 'bg-indigo-100 text-indigo-800';
      case 'system':
        return 'bg-slate-100 text-slate-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-medical-500 rounded-md"
      >
        {unreadCount > 0 ? (
          <BellIconSolid className="h-6 w-6" />
        ) : (
          <BellIcon className="h-6 w-6" />
        )}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 block h-5 w-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Bildirimler</h3>
            {unreadCount > 0 && (
              <p className="text-sm text-gray-500 mt-1">{unreadCount} okunmamış bildirim</p>
            )}
          </div>

          {isLoading ? (
            <div className="p-4 text-center text-gray-500">Yükleniyor...</div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500">Bildirim bulunmuyor</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xl">{getNotificationIcon(notification.type)}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${getNotificationColor(notification.type)}`}>
                          {notification.type === 'mobile_case' && 'Mobil Vaka'}
                          {notification.type === 'planned_case' && 'Planlı Vaka'}
                          {notification.type === 'organization' && 'Organizasyon'}
                          {notification.type === 'unassigned_case' && 'Ekip Atanmamış'}
                          {notification.type === 'team_assignment' && 'Vaka Atandı'}
                          {notification.type === 'system' && 'Sistem'}
                        </span>
                        {!notification.is_read && (
                          <span className="h-2 w-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">
                        {notification.title}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400">
                        {new Date(notification.created_at).toLocaleString('tr-TR', {
                          timeZone: 'Europe/Istanbul',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDismiss(e, notification.id)}
                      className="ml-2 text-gray-400 hover:text-red-500 focus:outline-none"
                      title="Sil"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;

